/**
 * BoardPage — what a board sticker resolves to when it is not in a drone.
 *
 * Scanning a board QR from the dashboard lookup answers "which drone is this
 * in?". When the answer is "none", that is not an error and not a dead end: the
 * board still has a batch, a source box, a flash history and a record of every
 * drone it has been paired to. That is this page.
 *
 * A paired board can land here too (from the "Board details" link on the lookup
 * result) — the installed-drone card just becomes a link instead of an empty
 * state. One page, two states, no branch in the caller.
 *
 * Data comes from GET /api/scanner/board/:uid/detail, which accepts either a
 * bare UID or a raw `ESC:<uid>` sticker payload, so the route param can be
 * whatever the camera decoded.
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../../apiHelpers';
import { formatTimestamp } from '../../utils/date-utils';
import ItemName from '../../components/ItemName';
import PageLayout from '../../components/ui/PageLayout';
import { Tag } from '../../components/ui/Tag';
import { CATEGORY, alpha } from '../../components/ui/categoryColors';
import { LogoSpinner } from '../../components/ui/LogoSpinner';
import { Panel } from '../../components/ui/Panel';
import { EmptyState } from '../../components/ui/EmptyState';
import { DataTable } from '../../components/table/DataTable';
import type { ColumnDef } from '@tanstack/react-table';

interface BoardBox {
  barcode: string | null;
  vendor: string | null;
  lot_number: string | null;
  purchase_order_ref: string | null;
  received_at: string | null;
  mpn: string | null;
  sku: string | null;
  description: string | null;
  /** Migration 068, joined by scanner-service. The item's NAME, which is what
   *  "what came out of this box" actually means to a person. */
  base_name: string | null;
  variant_detail: string | null;
  display_name: string | null;
}

interface BoardDetail {
  board: {
    id: string;
    board_type: string;
    primary_uid: string;
    secondary_uid: string | null;
    board_code: string | null;
    serialnumber: string | null;
    batch_number: string | null;
    batch_receive_date: string | null;
    created_at: string;
    retired_at: string | null;
    is_current: boolean;
    registered_by: string | null;
    box: BoardBox | null;
  };
  drone: {
    serialnumber: string;
    title: string | null;
    condition: string | null;
  } | null;
  flashes: Array<{
    operation_timestamp: string;
    station_id: string;
    firmware_file: string;
    flash_type: string;
    success: boolean;
    error_message: string | null;
    duration_seconds: string | null;
    app_version: string | null;
    operator_firstname: string | null;
    operator_lastname: string | null;
  }>;
  pairings: Array<{
    id: number;
    dateofservice: string;
    drone_serial: string;
    descript: string;
    operator_firstname: string | null;
    operator_lastname: string | null;
  }>;
}

const operatorName = (first: string | null, last: string | null) =>
  [first, last].filter(Boolean).join(' ') || 'Unknown';

type FlashRow = BoardDetail['flashes'][number];

// Module scope per the DataTable contract.
const FLASH_COLUMNS: ColumnDef<FlashRow, any>[] = [
  {
    id: 'when',
    header: 'When',
    meta: { cellClass: 'whitespace-nowrap text-on-surface-variant' },
    cell: ({ row }) => formatTimestamp(row.original.operation_timestamp, 'MMM D, YYYY h:mm A'),
  },
  { id: 'type', header: 'Type', cell: ({ row }) => row.original.flash_type },
  {
    id: 'firmware',
    header: 'Firmware',
    meta: { cellClass: 'break-all font-mono text-xs' },
    cell: ({ row }) => row.original.firmware_file,
  },
  {
    id: 'station',
    header: 'Station',
    meta: { cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.station_id,
  },
  {
    id: 'operator',
    header: 'Operator',
    meta: { cellClass: 'whitespace-nowrap text-on-surface-variant' },
    cell: ({ row }) => operatorName(row.original.operator_firstname, row.original.operator_lastname),
  },
  {
    id: 'result',
    header: 'Result',
    cell: ({ row }) => row.original.success ? (
      <Tag tone="good">OK</Tag>
    ) : (
      <Tag tone="bad" title={row.original.error_message || undefined}>Failed</Tag>
    ),
  },
];

const Card: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({
  title, children, className = '',
}) => (
  <Panel title={title} titleStyle="section" className={className}>{children}</Panel>
);

const Field: React.FC<{ label: string; children: React.ReactNode; mono?: boolean }> = ({
  label, children, mono = false,
}) => (
  <div className="min-w-0">
    <div className="text-[11px] uppercase tracking-wider text-on-surface-variant mb-1">{label}</div>
    <div className={`text-sm text-on-surface break-all ${mono ? 'font-mono' : ''}`}>{children}</div>
  </div>
);

const BoardPage: React.FC = () => {
  const { uid } = useParams<{ uid: string }>();
  const api = useApi();

  const [detail, setDetail] = useState<BoardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.get(`/scanner/board/${encodeURIComponent(uid)}/detail`)
      .then((data: BoardDetail) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        if (!cancelled) setError('No board is registered under this ID. It may never have been flashed.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
    // api identity is unstable by design — see the useEffect-deps note in CLAUDE.md
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  if (loading) return <LogoSpinner label="Loading the board" />;

  if (error || !detail) {
    return (
      <PageLayout kind="focus" className="pt-10">
        <EmptyState title="Board not found">
          <div className="mb-2 break-all font-mono text-lg text-on-surface">{uid}</div>
          <p className="m-0">{error}</p>
        </EmptyState>
      </PageLayout>
    );
  }

  const { board, drone, flashes, pairings } = detail;

  return (
    <PageLayout width="narrow" kind="record" className="pt-6 space-y-4">
      {/* Header — type + UID are the board's identity, so they lead */}
      <header className="flex flex-wrap items-center gap-3">
        <Tag tone="accent">{board.board_type}</Tag>
        <h1 className="font-mono text-xl text-white break-all">{board.primary_uid}</h1>
        {board.retired_at && (
          <Tag tone="bad">Retired</Tag>
        )}
      </header>

      {/* Installed-in — the question a scan is usually asking */}
      {drone ? (
        <Link
          to={`/drone/${drone.serialnumber}`}
          className="block rounded-md border p-4 transition-[filter] duration-200 hover:brightness-125"
          style={{ backgroundColor: alpha(CATEGORY.green.mark, 0.06), borderColor: alpha(CATEGORY.green.mark, 0.35) }}
        >
          <div className="text-[11px] uppercase tracking-widest mb-1" style={{ color: CATEGORY.green.ink.oklch }}>Installed in</div>
          <div className="font-mono text-lg" style={{ color: CATEGORY.green.ink.oklch }}>{drone.serialnumber}</div>
          {(drone.title || drone.condition) && (
            <div className="text-xs text-on-surface-variant mt-1">
              {[drone.title, drone.condition].filter(Boolean).join(' · ')}
            </div>
          )}
        </Link>
      ) : (
        <Panel padding="sm" label="Installed in">
          <div className="text-[11px] uppercase tracking-widest text-on-surface-variant mb-1">Installed in</div>
          <div className="text-base text-on-surface">Not in a drone</div>
          <p className="text-xs text-on-surface-variant mt-1">
            {pairings.length > 0
              ? 'This board has been paired before. Its history is below.'
              : 'This board has never been paired to a drone.'}
          </p>
        </Panel>
      )}

      <Card title="Board">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Type">{board.board_type}</Field>
          <Field label="Primary UID" mono>{board.primary_uid}</Field>
          {board.secondary_uid && <Field label="Secondary UID" mono>{board.secondary_uid}</Field>}
          <Field label="Batch">{board.batch_number || '—'}</Field>
          <Field label="Batch received">
            {board.batch_receive_date ? formatTimestamp(board.batch_receive_date) : '—'}
          </Field>
          <Field label="First registered">{formatTimestamp(board.created_at)}</Field>
          <Field label="Registered by">{board.registered_by || '—'}</Field>
          {board.board_code && <Field label="Board code" mono>{board.board_code}</Field>}
        </div>
      </Card>

      {/* Phase 1b traceability: which physical box this board came out of */}
      {board.box && (
        <Card title="Source box">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Field label="Box" mono>{board.box.barcode || '—'}</Field>
            <Field label="Vendor">{board.box.vendor || '—'}</Field>
            <Field label="Lot">{board.box.lot_number || '—'}</Field>
            {/* A traceability page IS a detail page, so the SKU stays: the
                name leads and the code sits muted under it. */}
            <Field label="Item"><ItemName size="sm" showSku item={board.box} /></Field>
            <Field label="MPN" mono>{board.box.mpn || '—'}</Field>
            <Field label="PO">{board.box.purchase_order_ref || '—'}</Field>
            <Field label="Received">
              {board.box.received_at ? formatTimestamp(board.box.received_at) : '—'}
            </Field>
          </div>
        </Card>
      )}

      <Card title={`Pairing history (${pairings.length})`}>
        {pairings.length === 0 ? (
          <p className="text-sm text-on-surface-variant">Never paired to a drone.</p>
        ) : (
          <ul className="space-y-2">
            {pairings.map((p) => (
              <li key={p.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                <span className="text-on-surface-variant text-xs w-40 shrink-0">
                  {formatTimestamp(p.dateofservice, 'MMM D, YYYY h:mm A')}
                </span>
                <Link to={`/drone/${p.drone_serial}`} className="font-mono text-primary hover:underline">
                  {p.drone_serial}
                </Link>
                <span className="text-on-surface-variant min-w-0 flex-1">{p.descript}</span>
                <span className="text-on-surface-variant text-xs">
                  {operatorName(p.operator_firstname, p.operator_lastname)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title={`Flash history (${flashes.length})`}>
        {flashes.length === 0 ? (
          <p className="text-sm text-on-surface-variant">No flash operations recorded for this board.</p>
        ) : (
          <DataTable<FlashRow>
            data={flashes}
            columns={FLASH_COLUMNS}
            getRowId={(f, i) => `${f.operation_timestamp}-${i}`}
            on="panel"
            density="compact"
            empty={null}
          />
        )}
      </Card>
    </PageLayout>
  );
};

export default BoardPage;
